<script setup lang="ts">
const containerRef = useTemplateRef("containerRef");

const { minScale, maxScale } = defineProps<{
  minScale: number;
  maxScale: number;
}>();

const ZOOM_SPEED = 0.2;
const SMOOTHING = 0.1;

const currentScale = ref(1);
const rafId = ref<number>();
const mouseX = ref(50);
const mouseY = ref(50);
const isMouseDown = ref(false);

const targetScale = defineModel<number>({ default: 1 });

const transformOrigin = computed(() => `${mouseX.value}% ${mouseY.value}%`);
const transform = computed(() => `scale(${currentScale.value})`);

const animateZoom = () => {
  currentScale.value += (targetScale.value - currentScale.value) * SMOOTHING;

  const THRESHOLD = 0.001;
  if (Math.abs(targetScale.value - currentScale.value) > THRESHOLD) {
    rafId.value = requestAnimationFrame(animateZoom);
  } else {
    rafId.value = undefined;
  }
};

const onWheelSpin = (e: WheelEvent) => {
  const delta = e.deltaY < 0 ? 1 : -1;
  targetScale.value = Math.min(
    maxScale,
    Math.max(minScale, targetScale.value + delta * ZOOM_SPEED),
  );
  if (!rafId.value) animateZoom();
};

const onMouseMove = useThrottleFn((e: MouseEvent) => {
  if (isMouseDown.value) return;

  const rect = containerRef.value!.getBoundingClientRect();
  mouseX.value = ((e.clientX - rect.left) / rect.width) * 100;
  mouseY.value = ((e.clientY - rect.top) / rect.height) * 100;
}, 10);

watch(targetScale, () => {
  animateZoom();
});

const onMouseDown = () => (isMouseDown.value = true);
const onMouseUp = () => (isMouseDown.value = false);

// Use pinch gesture
const { onPointerDown, onPointerMove, onPointerUp } = usePinchGesture(
  containerRef,
  {
    minScale: () => minScale,
    maxScale: () => maxScale,
    onZoom: (scale) => {
      targetScale.value = scale;
      if (!rafId.value) animateZoom();
    },
    onOriginChange: (xPercent, yPercent) => {
      mouseX.value = xPercent;
      mouseY.value = yPercent;
    },
  },
);
const handlePointerMove = (e: PointerEvent) => {
  e.preventDefault();
  onPointerMove(e);
};

onUnmounted(() => {
  if (rafId.value) {
    cancelAnimationFrame(rafId.value);
  }
});
</script>

<template>
  <div
    ref="containerRef"
    class="overflow-hidden select-none w-fit relative"
    :style="{ transform, transformOrigin }"
    @wheel.prevent="onWheelSpin"
    @mousemove="onMouseMove"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @pointerdown="onPointerDown"
    @pointermove="handlePointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @pointerleave="onPointerUp"
  >
    <div class="w-fit">
      <slot />
    </div>
  </div>
</template>
